import { useCallback, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMountedState } from 'react-use';
import { isEmpty, isNil, noop } from 'lodash';
import { SearchIcon } from 'tuui';
import uuid from 'uuid/v1';
import { resetAccounts } from '../../../actions';
import { useCurrentValueGetter } from '../../../hooks';
import { FormattedMessage } from '../../../modules/taboola-common-frontend-modules/i18n';
import AccountPickerLoader from '../../AccountPicker/AccountPickerLoader/AccountPickerLoader';
import AccountPickerRetryMenuItem from '../../AccountPicker/AccountPickerRetryMenuItem';
import styles from '../components/commonPickerDropdownStyle.module.scss';

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_TOTAL = -1;
export const useAsyncDropdownCache = ({
    fetcher,
    allOptionsValue,
    pageSize = DEFAULT_PAGE_SIZE,
    sortGetter = noop,
}) => {
    const [isOpen, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const searchGetter = useCurrentValueGetter(searchTerm);
    const isOpenGetter = useCurrentValueGetter(isOpen);
    const [options, setOptions] = useState([]);
    const optionsGetter = useCurrentValueGetter(options);
    const [totalCount, setTotalCount] = useState(EMPTY_TOTAL);
    const [error, setError] = useState(false);
    const hasMore = options.length < totalCount;
    const dispatch = useDispatch();

    const isMounted = useMountedState();

    const singleRequestCacheKey = useMemo(
        () => uuid(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isOpen, searchTerm]
    );

    const singleRequestCacheKeyGetter = useCurrentValueGetter(singleRequestCacheKey);
    const openDropdown = useCallback(() => {
        setOpen(true);
    }, []);

    const resetOptions = useCallback(() => {
        setOptions([]);
        setTotalCount(EMPTY_TOTAL);
        setError(false);
        dispatch(resetAccounts());
    }, [dispatch]);

    const closeDropdown = useCallback(() => {
        setOpen(false);

        if (searchGetter()) {
            resetOptions();
        }
    }, [searchGetter, resetOptions]);

    const getOptions = useCallback(
        inputValue =>
            new Promise((resolve, reject) => {
                const singleRequestCacheKeyMem = singleRequestCacheKeyGetter();

                const onSuccess = (fetchedOptions, hasMore, totalCount) => {
                    if (!isMounted() || !isOpenGetter() || singleRequestCacheKeyMem !== singleRequestCacheKeyGetter()) {
                        return;
                    }
                    const optionsToAdd =
                        isEmpty(optionsGetter()) && allOptionsValue
                            ? [allOptionsValue, ...fetchedOptions]
                            : fetchedOptions;
                    resolve({ options: optionsToAdd, hasMore });
                    setOptions(prev => [...prev, ...optionsToAdd]);
                    if (!isNil(totalCount)) {
                        setTotalCount(totalCount);
                    }
                };
                const onError = error => {
                    if (!isMounted() || !isOpenGetter() || singleRequestCacheKeyMem !== singleRequestCacheKeyGetter()) {
                        return;
                    }
                    setError(error);
                    reject(error);
                };

                fetcher({
                    searchText: `${inputValue}`.trim(),
                    pageSize,
                    onSuccess,
                    onError,
                    options: optionsGetter(),
                    sorting: sortGetter(),
                    singleRequestCacheKeyMem,
                });
            }),
        [
            isOpenGetter,
            fetcher,
            pageSize,
            sortGetter,
            optionsGetter,
            allOptionsValue,
            singleRequestCacheKeyGetter,
            isMounted,
        ]
    );

    const onInputChange = useCallback(
        searchTerm => {
            dispatch(resetAccounts());
            resetOptions();
            setSearchTerm(searchTerm);
        },
        [resetOptions, dispatch]
    );
    const onRetry = useCallback(() => {
        setError();
    }, []);

    return {
        menuIsOpen: true,
        manualMenuControl: true,
        autoFocus: true,
        searchable: true,
        controlShouldRenderValue: false,
        loadOptionsOnMenuOpen: !!searchTerm,
        hideSelectedOptions: false,
        tabSelectsValue: false,
        error,
        isOpen,
        openDropdown,
        closeDropdown,
        options,
        getOptions,
        resetOptions,
        hasMore,
        inputValue: searchTerm,
        totalCount,
        onInputChange,
        onRetry,
        arrowRenderer: () => <SearchIcon fontSize="small" className={styles['search-icon']} />,
        placeholder: <FormattedMessage id="app.collapsible.picker.search.placeholder" defaultMessage="Search" />,
        retryComponentRenderer: AccountPickerRetryMenuItem,
        loadingMessageRenderer: AccountPickerLoader,
    };
};
